import { getCandleCountForTimeframe, getTimeframeSeconds } from '../../utils/chartUtils'
import type { SavedDrawing } from './workbenchState'
import type { WalkthroughSpan } from '../../bot/api'

/**
 * Bars of run-up the user actually watches before the first drawing.
 *
 * Distinct from the SERVER's warm-up padding, which is much larger (~200 bars) so indicators
 * have real history to compute from. That padding belongs to the backtest range; this one
 * seeds the replay CURSOR, so the user lands a few bars before the action instead of
 * watching two hundred bars of dead warm-up.
 */
export const REPLAY_LEAD_BARS = 10

/**
 * Bars of context BEFORE the first drawing that the chart holds.
 *
 * Not a screenful — deliberately several screens. The opening viewport is one screen ending at
 * the cursor either way (see `useBacktestChartState`), so this is the history the user can pan
 * back into to read what the market was doing before the first drawing. A screenful left them
 * at the chart's left edge the moment they scrolled.
 *
 * Matches the server's `WARMUP_BARS`, which is the most history there can be: the run's range
 * starts at the UTC day containing `firstDrawing - WARMUP_BARS`, so asking for this much always
 * lands inside the range and the clamp below is a no-op in practice.
 */
export const DISPLAY_LEAD_BARS = 200

/**
 * Bars of context AFTER the last drawing that the chart holds.
 *
 * Mirrors the server's own `TRAIL_BARS`, which is the window it gave the engine to close
 * positions in. Showing less would put a real exit off the right edge of the chart — the
 * trade would appear in the pane with nowhere on the chart to have happened.
 */
export const DISPLAY_TRAIL_BARS = 20

/**
 * What the CHART loads, shows and can pan to — as opposed to the run's real range.
 *
 * The server's range is padded by ~200 bars of indicator warm-up and rounded out to whole UTC
 * days at both ends, so on an intraday timeframe it can be an order of magnitude wider than
 * the drawings: an M1 chart drawn on 02:00–04:00 gets a range covering the entire day, of
 * which the drawn two hours is under a tenth. Left as the chart's range that padding becomes
 * the progress bar's travel and hundreds of pannable bars of nothing.
 *
 * The padding itself is right and stays — the engine needs it, and the run is genuinely
 * computed over it. This is only the window the client draws.
 */
export interface DisplaySpan {
  /** unix seconds. The chart's left bound. */
  startTs: number
  /** unix seconds. The chart's right bound, and the transport's 100%. */
  endTs: number
}

/**
 * A chart window tight around the drawings.
 *
 * The lead is `DISPLAY_LEAD_BARS` rather than the `REPLAY_LEAD_BARS` the cursor uses, and the
 * two are deliberately different numbers: the cursor opens 10 bars before the first drawing so
 * playback starts just before the action, while the chart holds a few hundred bars of history
 * behind that — enough to fill the opening screen properly and to give the user somewhere to
 * pan back into and read the market before the first drawing. Never less than a screenful, or
 * the chart opens with those ten candles stretched across the whole pane. The bars between the
 * two are static context — the transport cannot reach them (see `progressWindowStartTs`),
 * which is why they cost nothing on the progress bar.
 *
 * Clamped into the run's real range, exactly as `replayStartTs` is. When the server's range is
 * itself narrower than the requested lead the two collapse to the same instant, which is fine:
 * there simply is no more history to show.
 */
export function displaySpan(
  span: DrawingSpan,
  timeframe: string,
  rangeStartTs: number,
  rangeEndTs: number,
): DisplaySpan {
  const tfSec = getTimeframeSeconds(timeframe)
  // Infinity: the second argument caps the count against how many bars exist, and here we are
  // asking the unbounded question "how many fit on screen at this timeframe". The floor only
  // bites on a timeframe whose screen is wider than the lead, which no current one is — it is
  // there so the "never opens with a stretched screen" guarantee survives either constant moving.
  const leadBars = Math.max(DISPLAY_LEAD_BARS, getCandleCountForTimeframe(timeframe, Infinity))
  const firstDrawingSec = Math.floor(span.fromTs / 1000)
  const lastDrawingSec = Math.floor(span.toTs / 1000)

  const clamp = (ts: number) => Math.max(rangeStartTs, Math.min(rangeEndTs, ts))
  return {
    startTs: clamp(firstDrawingSec - leadBars * tfSec),
    endTs: clamp(lastDrawingSec + DISPLAY_TRAIL_BARS * tfSec),
  }
}

/**
 * The raw drawing span the server needs, in MILLISECONDS.
 *
 * Timestamps live on SavedDrawing.points[].timestamp (klinecharts native ms). Points with no
 * timestamp are price-only anchors — a horizontal level line has a price but no position on
 * the time axis — and contribute nothing to the span.
 */
export interface DrawingSpan {
  /** Earliest drawn point, ms. */
  fromTs: number
  /** Latest drawn point, ms. */
  toTs: number
  /** How many drawings contributed a timestamp. */
  count: number
}

/**
 * Collapse the drawings to their time span.
 *
 * Returns null when nothing is anchored in time, which is a real case (a chart with only
 * horizontal levels) and the reason the Walkthrough guard cannot just count drawings.
 */
export function drawingSpan(drawings: SavedDrawing[]): DrawingSpan | null {
  let fromTs = Infinity
  let toTs = -Infinity
  let count = 0

  for (const d of drawings) {
    let contributed = false
    for (const p of d.points ?? []) {
      const ts = p?.timestamp
      if (typeof ts !== 'number' || !Number.isFinite(ts)) continue
      if (ts < fromTs) fromTs = ts
      if (ts > toTs) toTs = ts
      contributed = true
    }
    if (contributed) count += 1
  }

  if (count === 0) return null
  return { fromTs, toTs, count }
}

/** Build the request body the walkthrough endpoints take, or null if there is no span. */
export function walkthroughSpan(
  symbol: string,
  timeframe: string,
  drawings: SavedDrawing[],
): WalkthroughSpan | null {
  const span = drawingSpan(drawings)
  if (!span) return null
  return { symbol, timeframe, fromTs: span.fromTs, toTs: span.toTs }
}

/**
 * Where the replay should start, in unix SECONDS.
 *
 * The server's range begins far earlier than the drawings so indicators can warm up, but
 * `useBacktestChartState` would otherwise seed the cursor at rangeStartTs (or, for a
 * completed run with no saved cursor, at rangeEndTs — the fully-drawn end state). Neither is
 * what a walkthrough wants: it should open a few bars before the first drawing.
 *
 * Clamped into [rangeStartTs, rangeEndTs] so a drawing that sits outside the run's clamped
 * range can never park the cursor off the end of the data.
 */
export function replayStartTs(
  span: DrawingSpan,
  timeframe: string,
  rangeStartTs: number,
  rangeEndTs: number,
): number {
  const tfSec = getTimeframeSeconds(timeframe)
  const firstDrawingSec = Math.floor(span.fromTs / 1000)
  const lead = firstDrawingSec - REPLAY_LEAD_BARS * tfSec
  return Math.max(rangeStartTs, Math.min(rangeEndTs, lead))
}
